import { Component } from '@theme/component';

/**
 * Aviso informativo de cookies (no bloqueante).
 *
 * Al aceptar/cerrar guarda una cookie (`ae_cookie_consent`) durante
 * `data-remember-days`. Esa cookie es la fuente de verdad compartida: mientras
 * viva, ni el banner ni el newsletter modal se muestran. No gestiona
 * consentimiento real de tracking.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} accept
 * @property {HTMLButtonElement} close
 *
 * @extends {Component<Refs>}
 */
class CookieBanner extends Component {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.#hasConsent()) {
      this.remove();
      return;
    }

    this.#controller = new AbortController();
    this.ownerDocument.addEventListener('keydown', this.#onKeydown, {
      signal: this.#controller.signal,
    });

    this.#reveal();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
  }

  /** Store consent and dismiss the banner. */
  accept() {
    this.#store();
    this.#hide();
  }

  /** Store consent (X button / Escape) and dismiss the banner. */
  dismiss() {
    this.#store();
    this.#hide();
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape') this.dismiss();
  };

  #reveal() {
    this.hidden = false;
    // Force a frame so the transition plays from the hidden state.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.classList.add('is-visible'));
    });
  }

  #hide() {
    this.#controller?.abort();
    this.#controller = null;
    this.classList.remove('is-visible');

    const styles = window.getComputedStyle(this);
    const hasTransition = parseFloat(styles.transitionDuration) > 0;

    if (!hasTransition) {
      this.remove();
      return;
    }

    const done = () => this.remove();
    this.addEventListener('transitionend', done, { once: true });
    // Fallback in case transitionend never fires.
    window.setTimeout(done, 400);
  }

  /** @returns {string} Cookie name shared with the newsletter modal. */
  get #cookieName() {
    return this.getAttribute('data-storage-key') || 'ae_cookie_consent';
  }

  /** @returns {number} Consent cookie lifetime, in seconds. */
  get #rememberSeconds() {
    const days = Number(this.getAttribute('data-remember-days')) || 180;
    return days * 24 * 60 * 60;
  }

  /** @returns {boolean} True while the consent cookie is present. */
  #hasConsent() {
    return document.cookie.split(';').some((c) => c.trim().startsWith(`${this.#cookieName}=`));
  }

  /**
   * Writes the consent cookie — the single source of truth shared with the
   * newsletter modal. While it lives, neither the banner nor the modal show.
   */
  #store() {
    document.cookie = `${this.#cookieName}=1; max-age=${this.#rememberSeconds}; path=/; SameSite=Lax`;
  }
}

if (!customElements.get('cookie-banner')) {
  customElements.define('cookie-banner', CookieBanner);
}
